/* eslint-disable react/no-array-index-key, consistent-return, @typescript-eslint/no-shadow, prettier/prettier */
import { useCallback } from 'react';
import { formatListData } from 'commons-ui';
import { IConfigKeyDetail, IAnalysisRequest, IAnalysisResponse, IZkChangeLog, IAdminStats } from './types';
import { CONFIG_IMPACT_ENDPOINTS } from './constants';
import useApiClient from './apiClient';

/**
 * Service hook for Config Impact module.
 *
 * Uses useApiClient (custom wrapper) so API calls route to the
 * chirag-jain stage env namespace — scoped to this module only.
 */
const useService = () => {
	const { postData } = useApiClient();

	const fetchConfigKeys = useCallback(
		async ({ pageDetails, searchText }: any) => {
			const response = await postData({
				endpoint: CONFIG_IMPACT_ENDPOINTS.GET_CONFIG_KEYS,
				body: {
					search: searchText,
					page: pageDetails?.pageNo,
					limit: pageDetails?.pageSize
				}
			});
			const result = response?.data?.data;
			return { rowCount: result?.total || 0, data: formatListData(result?.data, 'keyName') };
		},
		[postData]
	);

	const fetchKeyDetail = useCallback(
		async (keyName: string, service?: string): Promise<IConfigKeyDetail> => {
			const body: Record<string, string> = { keyName };
			if (service) body.service = service;
			const response = await postData({
				endpoint: CONFIG_IMPACT_ENDPOINTS.GET_CONFIG_KEY_DETAIL,
				body
			});
			return response?.data?.data;
		},
		[postData]
	);

	const submitAnalysis = useCallback(
		async (request: IAnalysisRequest): Promise<IAnalysisResponse> => {
			const response = await postData({
				endpoint: CONFIG_IMPACT_ENDPOINTS.ANALYZE_IMPACT,
				body: request
			});
			return response?.data?.data;
		},
		[postData]
	);

	const fetchHistory = useCallback(
		async ({
			pageDetails,
			key,
			from,
			to
		}: {
			pageDetails?: any;
			key?: string;
			from?: string;
			to?: string;
		}): Promise<{ rowCount: number; data: IZkChangeLog[] }> => {
			const response = await postData({
				endpoint: CONFIG_IMPACT_ENDPOINTS.GET_HISTORY,
				body: {
					key,
					from,
					to,
					page: pageDetails?.pageNo,
					limit: pageDetails?.pageSize
				}
			});
			const result = response?.data?.data;
			return { rowCount: result?.total || 0, data: result?.data || [] };
		},
		[postData]
	);

	const fetchTimeline = useCallback(
		async (keyName: string): Promise<IZkChangeLog[]> => {
			const response = await postData({
				endpoint: CONFIG_IMPACT_ENDPOINTS.GET_TIMELINE,
				body: { keyName }
			});
			return response?.data?.data || [];
		},
		[postData]
	);

	const triggerRebuild = useCallback(
		async (options?: {
			services?: string[];
			incremental?: boolean;
		}): Promise<{ jobId: string; status: string }> => {
			const response = await postData({
				endpoint: CONFIG_IMPACT_ENDPOINTS.TRIGGER_REBUILD,
				body: {
					services: options?.services,
					incremental: options?.incremental
				}
			});
			return response?.data?.data;
		},
		[postData]
	);

	const fetchStats = useCallback(async (): Promise<IAdminStats> => {
		const response = await postData({
			endpoint: CONFIG_IMPACT_ENDPOINTS.GET_STATS,
			body: {}
		});
		return response?.data?.data;
	}, [postData]);

	return {
		fetchConfigKeys,
		fetchKeyDetail,
		submitAnalysis,
		fetchHistory,
		fetchTimeline,
		triggerRebuild,
		fetchStats
	};
};

export default useService;
